import PropTypes from 'prop-types';
import React, { Fragment, useEffect, useMemo, useState } from 'react';
import { PayButton } from '../../../PayButton';

const CURRENCIES = ['UAH', 'USD', 'EUR', 'RUB'];
const PAYMENT_URL = 'https://secure.wayforpay.com/pay';

const productShape = PropTypes.shape({
    name: PropTypes.string.isRequired,
    price: PropTypes.number.isRequired,
    count: PropTypes.number.isRequired,
});

const propTypes = {
    merchantAccount: PropTypes.string.isRequired,
    merchantDomainName: PropTypes.string,
    orderReference: PropTypes.string.isRequired,
    orderDate: PropTypes.number.isRequired,
    amount: PropTypes.number.isRequired,
    currency: PropTypes.oneOf(CURRENCIES),
    products: PropTypes.arrayOf(productShape).isRequired,
    returnUrl: PropTypes.string,
    serviceUrl: PropTypes.string,
    disabled: PropTypes.bool,
    createSignature: PropTypes.func.isRequired,
    orderPaymentTimeout: PropTypes.number,
};

const defaultProps = {
    merchantDomainName: document.location.hostname,
    orderPaymentTimeout: 0,
};

function WayforpayButton({
    merchantAccount,
    merchantDomainName,
    orderReference,
    orderDate,
    amount,
    currency,
    products,
    returnUrl,
    serviceUrl,
    createSignature,
    orderPaymentTimeout,
    disabled: rawDisabled,
}) {
    const productName = useMemo(() => products.map(({ name }) => name), [products]);
    const productPrice = useMemo(() => products.map(({ price }) => price), [products]);
    const productCount = useMemo(() => products.map(({ count }) => count), [products]);
    const [merchantSignature, setMerchantSignature] = useState('');
    const [disabled, setDisabled] = useState(true);
    const isDisabled = disabled || rawDisabled;

    useEffect(() => {
        async function request() {
            if (!rawDisabled) {
                setDisabled(true);

                try {
                    const json = await createSignature({
                        merchantAccount,
                        merchantDomainName,
                        orderReference,
                        orderDate,
                        amount,
                        currency,
                        productName,
                        productPrice,
                        productCount,
                    });

                    setMerchantSignature(json.merchantSignature);
                } finally {
                    setDisabled(false);
                }
            }
        }

        void request();
    }, [
        merchantAccount,
        merchantDomainName,
        orderReference,
        orderDate,
        amount,
        currency,
        productName,
        productPrice,
        productCount,
        createSignature,
    ]);

    return (
        <form method="POST" action={PAYMENT_URL}>
            <input type="hidden" name="merchantAccount" value={merchantAccount} />
            <input type="hidden" name="merchantDomainName" value={merchantDomainName} />
            <input type="hidden" name="merchantSignature" value={merchantSignature} />
            <input type="hidden" name="merchantTransactionType" value="AUTO" />
            <input type="hidden" name="merchantTransactionSecureType" value="AUTO" />
            <input type="hidden" name="orderReference" value={orderReference} />
            <input type="hidden" name="orderDate" value={orderDate} />
            <input type="hidden" name="amount" value={amount} />
            <input type="hidden" name="currency" value={currency} />
            <input type="hidden" name="orderTimeout" value={orderPaymentTimeout} />
            <input type="hidden" name="orderLifetime" value={orderPaymentTimeout} />
            {productName.map((name, index) => (
                <Fragment key={index}>
                    <input type="hidden" name="productName[]" value={name} />
                </Fragment>
            ))}
            {productPrice.map((price, index) => (
                <Fragment key={index}>
                    <input type="hidden" name="productPrice[]" value={price} />
                </Fragment>
            ))}
            {productCount.map((count, index) => (
                <Fragment key={index}>
                    <input type="hidden" name="productCount[]" value={count} />
                </Fragment>
            ))}
            {returnUrl && <input type="hidden" name="returnUrl" value={returnUrl} />}
            {serviceUrl && <input type="hidden" name="serviceUrl" value={serviceUrl} />}

            <PayButton disabled={isDisabled} provider="wayforpay" />
        </form>
    );
}

WayforpayButton.propTypes = propTypes;
WayforpayButton.defaultProps = defaultProps;

export default WayforpayButton;
